import React, { useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Box,
  useTheme,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PaletteIcon from "@mui/icons-material/Palette";

export interface FilterListItem {
  value: string;
  label: string;
  color?: string;
}

interface FilterListProps {
  items: FilterListItem[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  maxHeight?: number;
  showColorIndicator?: boolean;
  title?: string;
  pinnedValues?: string[];
  onTogglePin?: (value: string) => void;
  colorByMode?: string; // מצב הצביעה הנוכחי
  onColorByToggle?: () => void; // פונקציה להפעלת/כיבוי מצב הצביעה
  colorByValue?: string; // הערך שאמור להיות במצב colorBy כשכפתור הפלטה פעיל
}

export const FilterList: React.FC<FilterListProps> = ({
  items,
  selectedValues,
  onToggle,
  maxHeight = 250,
  showColorIndicator = false,
  title,
  pinnedValues = [],
  onTogglePin,
  colorByMode,
  onColorByToggle,
  colorByValue,
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  // האם כפתור הפלטה פעיל
  const isPaletteActive = colorByMode === colorByValue;

  const selectedItems = items.filter((item) =>
    selectedValues.includes(item.value)
  );
  const pinnedItems = items.filter((item) =>
    pinnedValues.includes(item.value)
  );

  // כשסגור: הצג checked או pinned (ללא כפילויות)
  const itemsWhenCollapsed = [...selectedItems];
  pinnedItems.forEach((pinnedItem) => {
    if (!selectedItems.find(item => item.value === pinnedItem.value)) {
      itemsWhenCollapsed.push(pinnedItem);
    }
  });

  const itemsToDisplay = isExpanded ? items : itemsWhenCollapsed;

  return (
    <Box>
      {title && (
        <Box
          sx={{
            mb: 1,
            bgcolor: theme.palette.grey[200],
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <IconButton size="small" sx={{ ml: 1 }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                textAlign: "right",
                color: theme.palette.text.primary,
                px: 2,
                py: 1,
                flex: 1,
              }}
            >
              {title}
              {!isExpanded && selectedItems.length > 0 && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, opacity: 0.7 }}
                >
                  ({selectedItems.length} נבחרו)
                </Typography>
              )}
            </Typography>
          </Box>

          {/* כפתור פלטה בקצה השמאלי (סוף השורה) */}
          {onColorByToggle && colorByValue && (
            <Tooltip title={isPaletteActive ? "כבה צביעה לפי מסנן זה" : "הפעל צביעה לפי מסנן זה"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onColorByToggle();
                }}
                sx={{
                  mr: 1,
                  bgcolor: isPaletteActive ? theme.palette.primary.light : 'transparent',
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <PaletteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <Collapse in={isExpanded || itemsWhenCollapsed.length > 0}>
        <List
          dense
          sx={{
           bgcolor: theme.palette.background.paper,
            borderRadius: 1,
            maxHeight,
            overflowY: "auto",
          }}
        >
          {itemsToDisplay.map((item) => {
            const isPinned = pinnedValues.includes(item.value);
            const showPinButton = onTogglePin && (isExpanded || !isPinned);
            return (
              <ListItem key={item.value} disablePadding>
                <ListItemButton onClick={() => onToggle(item.value)} dense>
                  <Checkbox
                    edge="end"
                    checked={selectedValues.includes(item.value)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    {showColorIndicator && item.color && (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: item.color,
                        }}
                      />
                    )}
                    <ListItemText primary={item.label} sx={{ textAlign: "right" }} />
                  </Box>
                  {showPinButton && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTogglePin(item.value);
                      }}
                      sx={{ mr: 1, p: 0.5 }}
                    >
                      {isPinned ? (
                        <PushPinIcon sx={{ fontSize: 16 }} color="primary" />
                      ) : (
                        <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
};
